import { useContext, useEffect, useMemo } from 'react'

import { isArray, isFunction } from 'lodash'

import { GlobalFiltersContext } from '../Providers/GlobalFiltersProvider'
import { InstitutionContext } from '../Providers/InstitutionProvider'
import { InstitutionGroupContext } from '../Providers/InstitutionGroupProvider'
import { firstOrDefault, getIdOrNull, isNonEmptyArray, isObject } from '../utils'

const useInternshipEntityFilters = () => {
  const { internshipFilters } = useContext(GlobalFiltersContext)
  const institutionContext = useContext(InstitutionContext)
  const institutionGroupContext = useContext(InstitutionGroupContext)

  const { hospitalDepartments = [], institutions, sectionToYears = [], sectors = [] } = internshipFilters

  const isGroupContext = isObject(institutionGroupContext)
  const groupInstitutionId = useMemo(
    () => isGroupContext ? getIdOrNull(isArray(institutions) ? firstOrDefault(institutions) : institutions) : null,
    [isGroupContext, institutions]
  )

  useEffect(() => {
    if (groupInstitutionId === null || !isNonEmptyArray(hospitalDepartments) || !isFunction(institutionGroupContext?.loadInstitutionSectors)) {
      return
    }

    if (!institutionGroupContext.sectorsByInstitution?.[groupInstitutionId] && !institutionGroupContext.institutionSectorsLoading?.[groupInstitutionId]) {
      institutionGroupContext.loadInstitutionSectors({ id: groupInstitutionId })
    }
  }, [groupInstitutionId, hospitalDepartments, institutionGroupContext?.sectorsByInstitution, institutionGroupContext?.institutionSectorsLoading])

  const allSectors = useMemo(
    () => isGroupContext
      ? (institutionGroupContext?.sectorsByInstitution?.[groupInstitutionId] ?? [])
      : [...(institutionContext?.sectors ?? []), ...(institutionContext?.archivedSectors ?? [])],
    [isGroupContext, institutionGroupContext?.sectorsByInstitution, groupInstitutionId, institutionContext?.sectors, institutionContext?.archivedSectors]
  )

  const resolvedSectors = useMemo(() => {
    if (isNonEmptyArray(sectors)) {
      return sectors
    }

    if (isNonEmptyArray(hospitalDepartments)) {
      return allSectors
        .filter(sector => hospitalDepartments.includes(getIdOrNull(sector.hospitalDepartment)))
        .map(getIdOrNull)
    }

    return []
  }, [allSectors, hospitalDepartments, sectors])

  return useMemo(
    () => ({ hospitalDepartments, sectionToYears, sectors: resolvedSectors }),
    [hospitalDepartments, sectionToYears, resolvedSectors]
  )
}

export default useInternshipEntityFilters
